import { colors } from '../helpers/colors';
import { getAssetOptionsForIssueType } from '../helpers/get-asset-options';
import { useEffect, useMemo } from 'react';

import { css } from '@compiled/react';

import { wacToken } from '@/libraries/design/foundations';

import { JiraIssue } from '../constants/types';
import { ANIMATED_CARD_ID, Assignee, EpicName, IssueTypeName, Priority } from '../constants/ui';
import { useAnimatedJiraBoardStore } from '../state';

const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	color: colors.text,
	padding: '20px',
	borderRadius: '10px',
	background: wacToken('color.neutral.light0'),
	width: '500px',
	height: '200px',
	boxShadow:
		'0px 14px 30px 0px rgba(0, 0, 0, 0.10), ' +
		'0px 55px 55px 0px rgba(0, 0, 0, 0.09), ' +
		'0px 125px 75px 0px rgba(0, 0, 0, 0.05), ' +
		'0px 222px 89px 0px rgba(0, 0, 0, 0.01), ' +
		'0px 347px 97px 0px rgba(0, 0, 0, 0.00)',
	overflow: 'hidden',
});
const cardTitleWrapper = css({
	width: 'fit-content',
});
const cardTitle = css({
	color: colors.black,
	fontSize: '24px',
	fontStyle: 'normal',
	fontWeight: '600',
	width: '100%',
	lineHeight: '60px',

	// animation
	overflow: 'hidden',
	borderInlineEnd: `4px solid ${colors.textSelected}`,
	whiteSpace: 'nowrap',

	['@keyframes typing']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: { width: '0' },
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: { width: '100%' },
	},
	['@keyframes blinkCaret']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: {
			borderColor: 'transparent',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['50%']: {
			borderColor: `${colors.textSelected}`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: { borderColor: 'transparent' },
	},
	animation: 'typing 2s steps(60,end),  blinkCaret 0.75s step-end infinite',
});
const issueTypeContainer = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: '5px',
});
const typePriorityAssigneeRow = css({
	display: 'flex',
	flexDirection: 'row',
});
const animateCard = css({
	['@keyframes growTypingCard']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: {
			maxHeight: '0px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: {
			maxHeight: '200px',
		},
	},
	['@keyframes shrinkTypingCard']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: {
			maxHeight: '200px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: {
			maxHeight: '0px',
		},
	},
	['@keyframes fadeOut']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['from']: {
			opacity: 1,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['to']: {
			opacity: 0,
		},
	},
	['@keyframes fadeIn']: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['from']: {
			opacity: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['to']: {
			opacity: 1,
		},
	},
	animation:
		'fadeIn 0.5s forwards, growTypingCard 1s forwards, shrinkTypingCard 1s forwards 3.2s, fadeOut 0.5s forwards 3.2s',
});
type AnimatedTypingCardProps = {
	card?: JiraIssue;
};

export const AnimatedTypingCard = (props: AnimatedTypingCardProps) => {
	// constants
	const id = ANIMATED_CARD_ID;
	const issueType = IssueTypeName.STORY;
	const [{ messages }, { animateIssue, nextAnimationStep }] = useAnimatedJiraBoardStore();
	const cardDetails: JiraIssue = useMemo(() => {
		return {
			summary: messages.animatedCardHeading,
			issueType: issueType,
			epicName: EpicName.FEATURES,
			assignee: Assignee.ALVIN,
			priority: Priority.HIGH,
			...props.card,
			id: id,
		};
	}, [id, issueType, messages.animatedCardHeading, props.card]);

	const issueTypeElement = getAssetOptionsForIssueType(cardDetails.issueType, messages, true);
	useEffect(() => {
		setTimeout(() => {
			nextAnimationStep();
			animateIssue(cardDetails);
		}, 4200);
	}, [animateIssue, cardDetails, nextAnimationStep]);

	return (
		<div css={[cardStyles, animateCard]} aria-describedby={id}>
			<div css={cardTitleWrapper}>
				<div css={cardTitle} id={id}>
					{cardDetails.summary}
				</div>
			</div>
			<div css={typePriorityAssigneeRow} className="type-priority-assignee-row">
				<div css={issueTypeContainer} className="issue-type-container">
					{issueTypeElement}
				</div>
			</div>
		</div>
	);
};
